/**
 * pages/Efficiency.tsx — the register findings, as trends
 * ========================================================
 * The weekly report states what a finding is once a week. This page shows
 * whether it is moving, which is the version anyone can act on.
 *
 * Every panel is driven by a *Series() function from registerFindings.ts — the
 * same arithmetic the report uses — so the two can never disagree.
 *
 * Three states per panel, and the middle one matters most:
 *   • data          → the chart
 *   • unsupported   → this meter model cannot measure it, and says so
 *   • needs config  → we can measure it but nobody has supplied the setting
 *
 * The third state is deliberately visible rather than hidden. A blank panel
 * naming exactly what it needs is the most reliable way to get that thing.
 */

import { useMemo, useState } from 'react';
import { Activity, Gauge, Info, Waves } from 'lucide-react';

import type { UseSheetData } from '@/hooks/useSheetData';
import { useMeterList } from '@/hooks/useMeterList';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';

import { MeterPicker } from '@/components/dashboard/MeterPicker';
import { PageLoadingSkeleton, PageErrorState, PageHeader } from '@/components/ui/PageState';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';

import {
  LeadingPfChart,
  VoltageThdChart,
} from '@/components/charts/EfficiencyCharts';

import {
  hasExtendedRegisterBlock,
  leadingPfSeries,
  thdSeries,
} from '@/lib/features/registerFindings';

interface EfficiencyPageProps { data: UseSheetData }

// ─── Panel shell ──────────────────────────────────────────────────────────────

interface PanelProps {
  icon: React.ReactNode;
  title: string;
  /** Plain-English explanation, revealed by the (i) button. */
  info: string;
  /** Shown under the chart — the one sentence a reader should take away. */
  takeaway?: string;
  /** When set, the chart is replaced by this explanation. */
  blocked?: { heading: string; detail: string };
  children?: React.ReactNode;
}

function Panel({ icon, title, info, takeaway, blocked, children }: PanelProps) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start gap-2.5 px-1">
          <span className="mt-0.5 text-brand-600">{icon}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="font-display text-sm font-semibold text-ink-900">{title}</h3>
              <button
                type="button"
                onClick={() => setShowInfo(v => !v)}
                aria-expanded={showInfo}
                aria-label={`What does "${title}" mean?`}
                className="shrink-0 rounded-full p-0.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </div>
            {showInfo ? (
              <p className="mt-1.5 rounded-lg bg-ink-50 px-2.5 py-2 text-[11px] leading-relaxed text-ink-600">
                {info}
              </p>
            ) : null}
          </div>
        </div>

        {blocked ? (
          <div className="rounded-lg border border-dashed border-ink-200 bg-ink-50/50 px-4 py-6 text-center">
            <p className="text-xs font-semibold text-ink-700">{blocked.heading}</p>
            <p className="mx-auto mt-1 max-w-sm text-[11px] leading-relaxed text-ink-500">
              {blocked.detail}
            </p>
          </div>
        ) : (
          children
        )}

        {!blocked && takeaway ? (
          <p className="px-1 text-[11px] leading-relaxed text-ink-500">{takeaway}</p>
        ) : null}
      </div>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EfficiencyPage({ data }: EfficiencyPageProps) {
  const { rows, status, error } = data;
  const { meters, selectedMeter, setSelectedMeter, meterRows } = useMeterList(rows);
  const { selectedSheet } = useAuth();
  const { t } = useLanguage();

  const meta = selectedSheet?.meta;

  const series = useMemo(() => ({
    extended: hasExtendedRegisterBlock(meterRows),
    leading: leadingPfSeries(meterRows),
    thd: thdSeries(meterRows),
  }), [meterRows, meta]);

  if (status === 'loading' && !rows.length) return <PageLoadingSkeleton />;
  if (status === 'error' && !rows.length) {
    return <PageErrorState message={error} onRetry={() => void data.refetch()} />;
  }

  const unsupported = {
    heading: t('Not available on this meter'),
    detail: t('This site uses an LK4405, which does not measure reactive energy or harmonic distortion. No software change can produce this reading — it needs a meter that takes the measurement.'),
  };

  // Latest points, used for the one-line takeaways under each chart.
  const worstThd = series.thd.reduce((m, p) => Math.max(m, p.worst), 0);
  const totalLeading = series.leading[series.leading.length - 1]?.cumulativeKvarh ?? 0;
  return (
    <div className="animate-fade-in space-y-5 pb-2 pt-3">
      <PageHeader
        title={t('Efficiency')}
        description={t('Where energy is being wasted — night-time load, capacitors, power quality and idle time — tracked day by day.')}
      />

      <section className="space-y-2">
        <div className="flex items-end justify-between px-1">
          <h2 className="font-display text-base font-semibold text-ink-900">{t('Meter overview')}</h2>
          <span className="flex items-center gap-1 text-[11px] text-ink-500">
            <Gauge className="h-3.5 w-3.5" />
            {meters.length} {meters.length === 1 ? t('meter') : t('meters')}
          </span>
        </div>
        {meters.length ? (
          <MeterPicker meters={meters} value={selectedMeter} onChange={setSelectedMeter} />
        ) : null}
      </section>

      {!meterRows.length ? (
        <Card>
          <EmptyState
            icon={<Gauge className="h-8 w-8" />}
            title={t('No data for this meter yet')}
            description={t('Try another meter or wait for the next refresh.')}
          />
        </Card>
      ) : (
        <div className="space-y-4">

          <Panel
            icon={<Activity className="h-4 w-4" />}
            title={t('Capacitor Bank Over-Correction')}
            info={t('Capacitors are switched in to correct power factor while machines run. If they stay in after the plant stops, the site starts pushing power back into the grid instead. Empty bars mean the bank is behaving; tall bars are the ones worth fixing.')}
            takeaway={series.leading.length
              ? `${totalLeading.toFixed(0)} kVArh ${t('of leading energy over the window.')}`
              : undefined}
            blocked={!series.extended ? unsupported
              : series.leading.length === 0
                ? { heading: t('No leading energy recorded'),
                    detail: t('The capacitive register has not advanced over this window, which is what a correctly compensated bank looks like.') }
                : undefined}
          >
            <LeadingPfChart data={series.leading} />
          </Panel>

          <Panel
            icon={<Waves className="h-4 w-4" />}
            title={t('Voltage Distortion (Power Quality)')}
            info={t('Drives, UPS units and other electronics distort the shape of the incoming voltage. Each line is one phase; the dashed line is the IEEE 519 safe limit. Staying above it overheats motors and causes nuisance trips.')}
            takeaway={worstThd > 0
              ? `${t('Worst reading over the window')}: ${worstThd.toFixed(1)}%${
                  worstThd > 8 ? ` — ${t('above the limit')}.` : ` — ${t('within the limit')}.`}
                 ${t('This is the aggregate limit only; per-frequency compliance needs faster measurement hardware.')}`
              : undefined}
            blocked={!series.extended ? unsupported
              : series.thd.length === 0
                ? { heading: t('No distortion readings in this window'), detail: t('The meter has not reported harmonic data for these days.') }
                : undefined}
          >
            <VoltageThdChart data={series.thd} />
          </Panel>

          <Panel
            icon={<Gauge className="h-4 w-4" />}
            title={t('Capacitor Health')}
            info={t('Capacitors weaken with age and deliver less correction than the rating printed on them. This compares what each stage actually delivers when it switches in against that nameplate rating.')}
            blocked={!series.extended ? unsupported : {
              heading: t('Waiting on commissioning data'),
              detail: t('The measurement works, but it needs each capacitor stage’s nameplate rating, its detuning percentage, and whether that rating is quoted at the panel terminals or at the capacitor can. Those come from the commissioning record. Until they are supplied this stays blank rather than guessing.'),
            }}
          />

        </div>
      )}
    </div>
  );
}
